import type { ReactNode } from 'react';
import type { RelationWithoutHydration } from '../../../lib/flotiq-client/types/api-response';
import type { StatusPillColors } from '../../StatusPill/StatusPill';

import { isValidElement } from 'react';
import moment from 'moment';

import { getTestProps } from '../../../lib/helpers';

import CheckCircle from '../../CheckCircle/CheckCircle';
import StatusPill from '../../StatusPill/StatusPill';
import RelationCellRender from './RelationCellRender';
import SingleMediaCellRender from './SingleMediaCellRender';

export const relationRenderer = (
  data: RelationWithoutHydration[],
  testId?: string,
) => {
  if (!data?.length) return null;

  if (
    data.length === 1 &&
    data[0].dataUrl.startsWith('/api/v1/content/_media/')
  ) {
    return <SingleMediaCellRender dataUrl={data[0].dataUrl} />;
  }

  return <RelationCellRender data={data} testId={testId} />;
};

export const geoRenderer = (data: { lat?: number; lon?: number }) => {
  if (!data) return null;
  return (
    <span className="inline-block h-full w-full truncate">
      {`${data.lat || '?'}, ${data.lon || '?'}`}
    </span>
  );
};

export const pillRenderer = (
  data: string | string[],
  color: StatusPillColors = 'blue',
) => {
  if (!data) return null;
  return Array.isArray(data) ? (
    <div className="w-full flex gap-1">
      {data.slice(0, 3).map((d, index) => (
        <StatusPill
          status={d}
          containerClasses="text-xs lg:text-sm lg:py-1.5 lg:px-3 lg:h-7 py-0.5 px-1 h-fit truncate"
          color={color}
          key={`${d}-${index}`}
        />
      ))}
      {data.length > 3 && (
        <div
          className="bg-blue px-2 py-0.5 text-white rounded-lg flex ml-1 justify-center items-center"
          data-testid="object-renderer-counter"
        >
          +{data.length - 3}
        </div>
      )}
    </div>
  ) : (
    <div className="inline-block truncate w-full">
      <StatusPill
        status={data}
        containerClasses="text-xs lg:text-sm lg:py-1.5 lg:px-3 lg:h-7 py-0.5 px-1 h-fit truncate"
        color={color}
      />
    </div>
  );
};

export const webhookActionsRenderer = (
  data: { action: string }[],
  color: StatusPillColors = 'blue',
  testId = '',
) => {
  if (!data) return null;
  return (
    <div className="flex flex-nowrap truncate w-full">
      {data?.map((el) => {
        if (!el.action) return null;
        return (
          <StatusPill
            key={el.action}
            status={el.action}
            containerClasses="text-xs lg:text-sm lg:py-1.5 lg:px-3 lg:h-7 py-0.5 px-1 h-fit truncate ml-1"
            color={color}
            testId={testId ? `${testId}-status-pill` : ''}
          />
        );
      })}
    </div>
  );
};

export const checkboxRenderer = (data: unknown, testId?: string) => {
  return defaultRenderer(
    typeof data !== 'undefined' ? (
      <CheckCircle
        checked={!!data}
        {...getTestProps(testId, 'checkbox', 'testId')}
      />
    ) : null,
  );
};

export const defaultRenderer = (data: unknown) => {
  return (
    <span className="inline-block w-full min-w-0 truncate">
      {isValidElement(data) ? data : data?.toString()}
    </span>
  );
};

export const dateRenderer = (date: string) => {
  if (date) return moment(date).format(date?.includes('T') ? 'L LT' : 'L');
  return '';
};

/**
 * Extract elements from list column type
 */
const extractListElements = (
  data: any[],
  items: {
    order: string[];
    propertiesConfig: {
      [key: string]: { inputType: string } & Record<string, any>;
    };
  },
): any[] => {
  if (data == null) return [];
  const [order, propertiesConfig] = [items.order, items.propertiesConfig];
  const firstData = data[0];
  return order.map((element) => {
    const childItems = propertiesConfig[element]?.items;
    if (childItems != null) {
      return extractListElements(firstData?.[element], childItems);
    }
    if (
      ['text', 'number', 'email'].includes(propertiesConfig[element].inputType)
    ) {
      return data[0]?.[element];
    } else if (propertiesConfig[element].inputType === 'geo') {
      return `${data[0]?.[element]?.lat || '?'}, ${
        data[0]?.[element]?.lon || '?'
      }`;
    }
    return null;
  });
};

const ListCounter = ({ children }: { children: ReactNode }) => (
  <div
    className="bg-blue px-2 py-0.5 text-white rounded-lg flex ml-1 justify-center items-center"
    data-testid="object-renderer-counter"
  >
    {children}
  </div>
);

export const objectRenderer = (
  data: any[],
  items: {
    order: string[];
    propertiesConfig: {
      [key: string]: { inputType: string } & Record<string, any>;
    };
  } | null,
) => {
  if (data == null || items == null) return null;
  const listElements = extractListElements(data, items)
    .flat()
    .filter((element) => element);
  return (
    <div className="flex items-center max-w-full">
      {defaultRenderer(listElements.join(', '))}
      {data.length >= 2 && listElements.length > 0 && (
        <ListCounter>+{data.length - 1}</ListCounter>
      )}
      {data.length >= 1 && listElements.length === 0 && (
        <ListCounter>{data.length}</ListCounter>
      )}
    </div>
  );
};
